// src/components/customers/InvoicesPopover.jsx
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * InvoicesPopover Component
 *
 * A popover component that displays customer invoices in a dropdown format.
 * Shows invoice details and provides quick access to invoice information.
 *
 * @param {Object} props - Component props
 * @param {Array} props.invoices - Array of invoice objects
 * @param {string} props.jobId - Current job ID
 * @param {string} props.customerId - Customer ID
 * @param {string} props.customerName - Customer name for display
 */
const InvoicesPopover = ({ invoices, jobId, customerId, customerName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef();
  const buttonRef = useRef();

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Handle escape key to close popover
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // Format currency display
  const formatCurrency = (amount, currency = "GBP") => {
    if (!amount) return `${currency} 0.00`;
    return `${currency} ${parseFloat(amount).toFixed(2)}`;
  };

  // Format date display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (error) {
      return dateString;
    }
  };

  // Filter invoices related to this specific job only

  const jobInvoices = invoices.filter(
    (invoice) => invoice.jobId === jobId
  );

  if (invoices && invoices.length > 0) {
    invoices.forEach((inv, index) => {
    });
  }

  // Toggle popover visibility
  const togglePopover = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative w-full sm:w-auto">
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={togglePopover}
        className="w-full btn flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
        title="View customer invoices"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Invoice icon */}
        <svg
          className="w-5 h-5 mr-2 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        Show Invoices
        {jobInvoices.length > 0 && (
          <span className="ml-2 bg-blue-800 text-white text-xs px-2 py-1 rounded-full">
            {jobInvoices.length}
          </span>
        )}
        {/* Dropdown arrow */}
        <svg
          className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Popover Content */}
      {isOpen && (
        <div
          ref={popoverRef}
          className="absolute left-0 sm:right-0 sm:left-auto mt-2 w-full sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 transform transition-all duration-200"
          role="menu"
          aria-orientation="vertical"
        >
          {/* Popover Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Select Invoice
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                aria-label="Close invoices popover"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <p className="text-sm text-blue-600 mt-1">
              {jobInvoices.length} invoice{jobInvoices.length !== 1 ? "s" : ""}
            </p>
          </div>

          {/* Popover Body */}
          <div className="max-h-80 overflow-y-auto">
            {jobInvoices.length === 0 ? (
              /* Empty State */
              <div className="px-4 py-8 text-center">
                <svg
                  className="w-12 h-12 mx-auto text-gray-400 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-gray-500 text-sm mb-2">No invoices found</p>
                <p className="text-gray-400 text-xs">
                  Create an invoice for this customer to see it here
                </p>
              </div>
            ) : (
              /* Invoice List */
              <div className="py-2">
                {jobInvoices.map((invoice, index) => (
                  <div
                    key={invoice.id || index}
                    className="px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                  >
                    {/* Invoice Header */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <svg
                            className="w-4 h-4 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {invoice.invoiceNumber ||
                              invoice.number ||
                              invoice.invoice ||
                              `Invoice #${invoice.id}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-blue-600">
                          {formatCurrency(invoice.total || invoice.amount)}
                        </p>
                      </div>
                    </div>

                    {/* Invoice Details */}
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="text-gray-500">Date:</span>{" "}
                        <span className="font-medium">
                          {formatDate(invoice.date || invoice.createdAt)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Due:</span>{" "}
                        <span className="font-medium">
                          {formatDate(invoice.dueDate)}
                        </span>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="mt-2 text-sm text-gray-600">
                      <span className="text-gray-500">Items:</span>{" "}
                      <span className="font-medium">
                        {invoice.items?.length || invoice.itemCount || 1}
                      </span>
                    </div>

                    {/* Action Buttons - Using React Router Link */}
                    <div className="mt-3 flex gap-2">
                      <Link
                        to={`/invoices/${invoice.invoice || invoice.id}`}
                        className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-colors duration-150 inline-block"
                      >
                        View Details
                      </Link>
                      {invoice.status !== "paid" && (
                        <button
                          type="button"
                          className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors duration-150"
                        >
                          Mark Paid
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Popover Footer - Using React Router Link */}
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Total:{" "}
                <span className="font-semibold text-gray-900">
                  {formatCurrency(
                    jobInvoices.reduce(
                      (sum, inv) =>
                        sum + parseFloat(inv.total || inv.amount || 0),
                      0
                    )
                  )}
                </span>
              </div>
              <Link
                to={`/invoices/add?customer=${customerId}&job=${jobId}`}
                className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors duration-150 inline-block"
              >
                + New Invoice
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPopover;
